import React from 'react'
import styled from '@emotion/styled'
import { Divider, Stack, Typography } from '@mui/material'
import { Inputs } from '../../../containers/facilityReservationTodayConditionInput/facilityReservationTodayConditionInputService'
import { Control } from 'react-hook-form'
import { GetTodayConditionInputDetailDto } from '../../../dataAccess/webApi/dto/reservationsDto'
import { GContainer, GItem } from '../common/grids'
import { TitleLabel } from '../common/titleLabel'
import { AttentionLabel } from '../common/attentionLabel'
import { NumberBox } from '../common/inputs/numberBox'
import { TextBox } from '../common/inputs/textBox'
import { RadioButtonGroup } from '../common/inputs/radioButtonGroup'
import { CheckBox } from '../common/inputs/checkBox'
import { TimePicker } from '../common/inputs/timePicker'
import { todayConditionInputFieldUsage, yesNo } from '../../../containers/common/constant/classification'
import { translate } from '../../../i18n'
import TodayInputDecimalTypeField from './common/todayInputDecimalTypeField'

const SectionText = styled(Typography)(() => ({
  fontSize: '21px',
  marginTop: '10px',
}))

const BlackDivider = styled(Divider)(() => ({
  borderColor: '#000',
  marginBottom: '20px',
}))

type TodayConditionSecProps = {
  control: Control<Inputs>
  todayConditionInputFieldsInfo: GetTodayConditionInputDetailDto['todayConditionInputFieldsInfo']
  isCough: boolean
  isRunnyNose: boolean
  isEczema: boolean
  isAllergy: boolean
  isFoodAllergy: boolean
  isMedicine: boolean
  isMood: boolean
  isDisease: boolean
  isVaccine: boolean
  isHospitalVisit: boolean
  isInfection: boolean
  isTodayConditionPoint: boolean
  yesNos: {
    value: string
    label: string
  }[]
}

// きょうの様子セクション
const TodayConditionSec: React.FC<TodayConditionSecProps> = (props) => {
  const {
    control,
    todayConditionInputFieldsInfo,
    isCough,
    isRunnyNose,
    isEczema,
    isAllergy,
    isFoodAllergy,
    isMedicine,
    isMood,
    isDisease,
    isVaccine,
    isHospitalVisit,
    isInfection,
    isTodayConditionPoint,
    yesNos,
  } = props

  return (
    <GItem xs={12}>
      {/* セクションタイトル部分 */}
      <GContainer>
        <GItem xs={12}>
          <SectionText>{translate('facilityReservationTodayConditionInput.section.todayCondition')}</SectionText>
          <BlackDivider />
        </GItem>
      </GContainer>
      {/* フォーム部分 */}
      <Stack spacing={2}>
        {/* きょうの様子 > 体温（登園前） */}
        {todayConditionInputFieldsInfo &&
          todayConditionInputFieldsInfo.morningBodyTemperature !== todayConditionInputFieldUsage.noUse && (
            <>
              {/* 体温（登園前） */}
              <TodayInputDecimalTypeField
                name={"morningBodyTemperature"}
                title={"facilityReservationTodayConditionInput.form.label.morningBodyTemperature.field"}
                intLabel={"facilityReservationTodayConditionInput.form.label.morningBodyTemperature.intPart"}
                fracLabel={"facilityReservationTodayConditionInput.form.label.morningBodyTemperature.fracPart"}
                unit={"facilityReservationTodayConditionInput.form.label.morningBodyTemperature.unit"}
                required={todayConditionInputFieldsInfo.morningBodyTemperature === todayConditionInputFieldUsage.necessary}
                control={control}
                intMax={43}
                intMin={35}
                intPlaceholder={37}
                fracMac={9}
                fracMin={0}
                fracPlaceholder={0}
              />

              {/* 計測時刻（登園前） */}
              <GItem>
                <TitleLabel>
                  {todayConditionInputFieldsInfo.morningBodyTemperature === todayConditionInputFieldUsage.necessary && (
                    <AttentionLabel>*</AttentionLabel>
                  )}
                  {translate('facilityReservationTodayConditionInput.form.label.morningBodyTemperatureMeasurementTime')}
                </TitleLabel>
                <GContainer alignItems="flex-end">
                  <GItem xs={12}>
                    <GItem sx={{ width: { xs: '100px', sm: '160px' }}}>
                      <TimePicker
                        name={'morningBodyTemperatureMeasurementTime'}
                        required={
                          todayConditionInputFieldsInfo.morningBodyTemperature === todayConditionInputFieldUsage.necessary
                        }
                        control={control}
                      />
                    </GItem>
                  </GItem>
                </GContainer>
              </GItem>
            </>
          )}

        {/* きょうの様子 > 体温（午前） */}
        {todayConditionInputFieldsInfo &&
          todayConditionInputFieldsInfo.afterComingBodyTemperature !== todayConditionInputFieldUsage.noUse && (
            <TodayInputDecimalTypeField
              name={"afterComingAmBodyTemperature"}
              title={"facilityReservationTodayConditionInput.form.label.afterComingAmBodyTemperature.field"}
              intLabel={"facilityReservationTodayConditionInput.form.label.afterComingAmBodyTemperature.intPart"}
              fracLabel={"facilityReservationTodayConditionInput.form.label.afterComingAmBodyTemperature.fracPart"}
              unit={"facilityReservationTodayConditionInput.form.label.afterComingAmBodyTemperature.unit"}
              required={todayConditionInputFieldsInfo.afterComingBodyTemperature === todayConditionInputFieldUsage.necessary}
              control={control}
              intMax={43}
              intMin={35}
              intPlaceholder={37}
              fracMac={9}
              fracMin={0}
              fracPlaceholder={0}
            />
          )}

        {/* きょうの様子 > 体温（午後） */}
        {todayConditionInputFieldsInfo &&
          todayConditionInputFieldsInfo.afterComingBodyTemperature !== todayConditionInputFieldUsage.noUse && (
            <TodayInputDecimalTypeField
              name={"afterComingPmBodyTemperature"}
              title={"facilityReservationTodayConditionInput.form.label.afterComingPmBodyTemperature.field"}
              intLabel={"facilityReservationTodayConditionInput.form.label.afterComingPmBodyTemperature.intPart"}
              fracLabel={"facilityReservationTodayConditionInput.form.label.afterComingPmBodyTemperature.fracPart"}
              unit={"facilityReservationTodayConditionInput.form.label.afterComingPmBodyTemperature.unit"}
              required={todayConditionInputFieldsInfo.afterComingBodyTemperature === todayConditionInputFieldUsage.necessary}
              control={control}
              intMax={43}
              intMin={35}
              intPlaceholder={37}
              fracMac={9}
              fracMin={0}
              fracPlaceholder={0}
            />
          )}

        {/* きょうの様子 > 咳 */}
        {todayConditionInputFieldsInfo &&
          todayConditionInputFieldsInfo.cough !== todayConditionInputFieldUsage.noUse && (
            <>
              {/* 咳をしていますか */}
              <GItem>
                <TitleLabel>
                  {todayConditionInputFieldsInfo.cough === todayConditionInputFieldUsage.necessary && (
                    <AttentionLabel>*</AttentionLabel>
                  )}
                  {translate('facilityReservationTodayConditionInput.form.label.cough')}
                </TitleLabel>
                <GContainer alignItems="flex-end">
                  <GItem xs={12}>
                    <RadioButtonGroup
                      name={'cough'}
                      label={translate('facilityReservationTodayConditionInput.form.label.cough')}
                      row
                      required={todayConditionInputFieldsInfo.cough === todayConditionInputFieldUsage.necessary}
                      buttonValueLabels={yesNos}
                      control={control}
                    />
                  </GItem>
                </GContainer>
              </GItem>

              {/* 咳に関する備考 */}
              {isCough && (
                <GItem>
                  <TitleLabel>{translate('facilityReservationTodayConditionInput.form.label.coughNote')}</TitleLabel>
                  <GContainer alignItems="flex-end">
                    <GItem xs={12}>
                      <TextBox
                        name="coughNote"
                        label={translate('facilityReservationTodayConditionInput.form.label.coughNote')}
                        maxLength={1000}
                        multiline
                        rows={4}
                        fullWidth
                        control={control}
                      />
                    </GItem>
                  </GContainer>
                </GItem>
              )}
            </>
          )}

        {/* きょうの様子 > 鼻水 */}
        {todayConditionInputFieldsInfo &&
          todayConditionInputFieldsInfo.runnyNose !== todayConditionInputFieldUsage.noUse && (
            <>
              {/* 鼻水がでていますか */}
              <GItem>
                <TitleLabel>
                  {todayConditionInputFieldsInfo.runnyNose === todayConditionInputFieldUsage.necessary && (
                    <AttentionLabel>*</AttentionLabel>
                  )}
                  {translate('facilityReservationTodayConditionInput.form.label.runnyNose')}
                </TitleLabel>
                <GContainer alignItems="flex-end">
                  <GItem xs={12}>
                    <RadioButtonGroup
                      name={'runnyNose'}
                      label={translate('facilityReservationTodayConditionInput.form.label.runnyNose')}
                      row
                      required={todayConditionInputFieldsInfo.runnyNose === todayConditionInputFieldUsage.necessary}
                      buttonValueLabels={yesNos}
                      control={control}
                    />
                  </GItem>
                </GContainer>
              </GItem>

              {/* 鼻水に関する備考 */}
              {isRunnyNose && (
                <GItem>
                  <TitleLabel>
                    {translate('facilityReservationTodayConditionInput.form.label.runnyNoseNote')}
                  </TitleLabel>
                  <GContainer alignItems="flex-end">
                    <GItem xs={12}>
                      <TextBox
                        name="runnyNoseNote"
                        label={translate('facilityReservationTodayConditionInput.form.label.runnyNoseNote')}
                        maxLength={1000}
                        multiline
                        rows={4}
                        fullWidth
                        control={control}
                      />
                    </GItem>
                  </GContainer>
                </GItem>
              )}
            </>
          )}

        {/* きょうの様子 > 湿疹 */}
        {todayConditionInputFieldsInfo &&
          todayConditionInputFieldsInfo.eczema !== todayConditionInputFieldUsage.noUse && (
            <>
              {/* 湿疹がでていますか */}
              <GItem>
                <TitleLabel>
                  {todayConditionInputFieldsInfo.eczema === todayConditionInputFieldUsage.necessary && (
                    <AttentionLabel>*</AttentionLabel>
                  )}
                  {translate('facilityReservationTodayConditionInput.form.label.eczema')}
                </TitleLabel>
                <GContainer alignItems="flex-end">
                  <GItem xs={12}>
                    <RadioButtonGroup
                      name={'eczema'}
                      label={translate('facilityReservationTodayConditionInput.form.label.eczema')}
                      row
                      required={todayConditionInputFieldsInfo.eczema === todayConditionInputFieldUsage.necessary}
                      buttonValueLabels={yesNos}
                      control={control}
                    />
                  </GItem>
                </GContainer>
              </GItem>

              {/* 使用している軟膏名 */}
              {isEczema && (
                <GItem>
                  <TitleLabel>
                    {todayConditionInputFieldsInfo.eczema === todayConditionInputFieldUsage.necessary && isEczema && (
                      <AttentionLabel>*</AttentionLabel>
                    )}
                    {translate('facilityReservationTodayConditionInput.form.label.eczemaName')}
                  </TitleLabel>
                  <GContainer alignItems="flex-end">
                    <GItem xs={12}>
                      <TextBox
                        name="eczemaName"
                        label={translate('facilityReservationTodayConditionInput.form.label.eczemaName')}
                        maxLength={200}
                        fullWidth
                        required={
                          todayConditionInputFieldsInfo.eczema === todayConditionInputFieldUsage.necessary && isEczema
                        }
                        control={control}
                      />
                    </GItem>
                  </GContainer>
                </GItem>
              )}
            </>
          )}

        {/* きょうの様子 > アレルギー */}
        {todayConditionInputFieldsInfo &&
          todayConditionInputFieldsInfo.allergy !== todayConditionInputFieldUsage.noUse && (
            <>
              {/* アレルギーがありますか */}
              <GItem>
                <TitleLabel>
                  {todayConditionInputFieldsInfo.allergy === todayConditionInputFieldUsage.necessary && (
                    <AttentionLabel>*</AttentionLabel>
                  )}
                  {translate('facilityReservationTodayConditionInput.form.label.allergy')}
                </TitleLabel>
                <GContainer alignItems="flex-end">
                  <GItem xs={12}>
                    <RadioButtonGroup
                      name={'allergy'}
                      label={translate('facilityReservationTodayConditionInput.form.label.allergy')}
                      row
                      required={todayConditionInputFieldsInfo.allergy === todayConditionInputFieldUsage.necessary}
                      buttonValueLabels={yesNos}
                      control={control}
                    />
                  </GItem>
                </GContainer>
              </GItem>

              {/* アレルギーに関する備考 */}
              {isAllergy && (
                <GItem>
                  <TitleLabel>{translate('facilityReservationTodayConditionInput.form.label.allergyNote')}</TitleLabel>
                  <GContainer alignItems="flex-end">
                    <GItem xs={12}>
                      <TextBox
                        name="allergyNote"
                        label={translate('facilityReservationTodayConditionInput.form.label.allergyNote')}
                        maxLength={1000}
                        multiline
                        rows={4}
                        fullWidth
                        control={control}
                      />
                    </GItem>
                  </GContainer>
                </GItem>
              )}
            </>
          )}

        {/* きょうの様子 > 食物アレルギー */}
        {todayConditionInputFieldsInfo &&
          todayConditionInputFieldsInfo.foodAllergy !== todayConditionInputFieldUsage.noUse && (
            <>
              {/* 食物アレルギーがありますか */}
              <GItem>
                <TitleLabel>
                  {todayConditionInputFieldsInfo.foodAllergy === todayConditionInputFieldUsage.necessary && (
                    <AttentionLabel>*</AttentionLabel>
                  )}
                  {translate('facilityReservationTodayConditionInput.form.label.foodAllergy')}
                </TitleLabel>
                <GContainer alignItems="flex-end">
                  <GItem xs={12}>
                    <RadioButtonGroup
                      name={'foodAllergy'}
                      label={translate('facilityReservationTodayConditionInput.form.label.foodAllergy')}
                      row
                      required={todayConditionInputFieldsInfo.foodAllergy === todayConditionInputFieldUsage.necessary}
                      buttonValueLabels={yesNos}
                      control={control}
                    />
                  </GItem>
                </GContainer>
              </GItem>

              {/* 食物アレルギーに関する備考 */}
              {isFoodAllergy && (
                <GItem>
                  <TitleLabel>
                    {translate('facilityReservationTodayConditionInput.form.label.foodAllergyNote')}
                  </TitleLabel>
                  <GContainer alignItems="flex-end">
                    <GItem xs={12}>
                      <TextBox
                        name="foodAllergyNote"
                        label={translate('facilityReservationTodayConditionInput.form.label.foodAllergyNote')}
                        maxLength={1000}
                        multiline
                        rows={4}
                        fullWidth
                        control={control}
                      />
                    </GItem>
                  </GContainer>
                </GItem>
              )}
            </>
          )}

        {/* きょうの様子 > 薬 */}
        {todayConditionInputFieldsInfo &&
          todayConditionInputFieldsInfo.medicine !== todayConditionInputFieldUsage.noUse && (
            <>
              {/* 薬を飲んでいますか */}
              <GItem>
                <TitleLabel>
                  {todayConditionInputFieldsInfo.medicine === todayConditionInputFieldUsage.necessary && (
                    <AttentionLabel>*</AttentionLabel>
                  )}
                  {translate('facilityReservationTodayConditionInput.form.label.medicine')}
                </TitleLabel>
                <GContainer alignItems="flex-end">
                  <GItem xs={12}>
                    <RadioButtonGroup
                      name={'medicine'}
                      label={translate('facilityReservationTodayConditionInput.form.label.medicine')}
                      row
                      required={todayConditionInputFieldsInfo.medicine === todayConditionInputFieldUsage.necessary}
                      buttonValueLabels={yesNos}
                      control={control}
                    />
                  </GItem>
                </GContainer>
              </GItem>

              {/* 服用している薬名 */}
              {isMedicine && (
                <GItem>
                  {todayConditionInputFieldsInfo.medicine === todayConditionInputFieldUsage.necessary && isMedicine && (
                    <AttentionLabel>*</AttentionLabel>
                  )}
                  <TitleLabel>{translate('facilityReservationTodayConditionInput.form.label.medicineName')}</TitleLabel>
                  <GContainer alignItems="flex-end">
                    <GItem xs={12}>
                      <TextBox
                        name="medicineName"
                        label={translate('facilityReservationTodayConditionInput.form.label.medicineName')}
                        maxLength={200}
                        fullWidth
                        required={
                          todayConditionInputFieldsInfo.medicine === todayConditionInputFieldUsage.necessary &&
                          isMedicine
                        }
                        control={control}
                      />
                    </GItem>
                  </GContainer>
                </GItem>
              )}
            </>
          )}

        {/* きょうの様子 > 機嫌 */}
        {todayConditionInputFieldsInfo &&
          todayConditionInputFieldsInfo.mood !== todayConditionInputFieldUsage.noUse && (
            <>
              {/* 機嫌は良いですか */}
              <GItem>
                <TitleLabel>
                  {todayConditionInputFieldsInfo.mood === todayConditionInputFieldUsage.necessary && (
                    <AttentionLabel>*</AttentionLabel>
                  )}
                  {translate('facilityReservationTodayConditionInput.form.label.mood')}
                </TitleLabel>
                <GContainer alignItems="flex-end">
                  <GItem xs={12}>
                    <RadioButtonGroup
                      name={'mood'}
                      label={translate('facilityReservationTodayConditionInput.form.label.mood')}
                      row
                      required={todayConditionInputFieldsInfo.mood === todayConditionInputFieldUsage.necessary}
                      buttonValueLabels={yesNos}
                      control={control}
                    />
                  </GItem>
                </GContainer>
              </GItem>

              {/* 機嫌に関する備考 */}
              {isMood && (
                <GItem>
                  <TitleLabel>{translate('facilityReservationTodayConditionInput.form.label.moodNote')}</TitleLabel>
                  <GContainer alignItems="flex-end">
                    <GItem xs={12}>
                      <TextBox
                        name="moodNote"
                        label={translate('facilityReservationTodayConditionInput.form.label.moodNote')}
                        maxLength={1000}
                        multiline
                        rows={4}
                        fullWidth
                        control={control}
                      />
                    </GItem>
                  </GContainer>
                </GItem>
              )}
            </>
          )}

        {/* きょうの様子 > 病気 */}
        {todayConditionInputFieldsInfo &&
          todayConditionInputFieldsInfo.disease !== todayConditionInputFieldUsage.noUse && (
            <>
              {/* いままで病気にかかったことはありますか */}
              <GItem>
                <TitleLabel>
                  {todayConditionInputFieldsInfo.disease === todayConditionInputFieldUsage.necessary && (
                    <AttentionLabel>*</AttentionLabel>
                  )}
                  {translate('facilityReservationTodayConditionInput.form.label.disease')}
                </TitleLabel>
                <GContainer alignItems="flex-end">
                  <GItem xs={12}>
                    <RadioButtonGroup
                      name={'disease'}
                      label={translate('facilityReservationTodayConditionInput.form.label.disease')}
                      row
                      required={todayConditionInputFieldsInfo.disease === todayConditionInputFieldUsage.necessary}
                      buttonValueLabels={yesNos}
                      control={control}
                    />
                  </GItem>
                </GContainer>
              </GItem>

              {isDisease && (
                <>
                  {/* 1か月以内にかかった病気ですか */}
                  <GItem>
                    <TitleLabel>
                      {todayConditionInputFieldsInfo.disease === todayConditionInputFieldUsage.necessary &&
                        isDisease && <AttentionLabel>*</AttentionLabel>}
                      {translate('facilityReservationTodayConditionInput.form.label.diseaseWithinMonth.field')}
                    </TitleLabel>
                    <GContainer alignItems="flex-end">
                      <GItem xs={12}>
                        <CheckBox
                          name={'diseaseWithinMonth'}
                          label={translate(
                            'facilityReservationTodayConditionInput.form.label.diseaseWithinMonth.field'
                          )}
                          valueAtCheck={yesNo.yes}
                          control={control}
                        >
                          {translate(
                            'facilityReservationTodayConditionInput.form.label.diseaseWithinMonth.chackBoxYes'
                          )}
                        </CheckBox>
                      </GItem>
                    </GContainer>
                  </GItem>

                  {/* 病気に関する備考 */}
                  <GItem>
                    <TitleLabel>
                      {translate('facilityReservationTodayConditionInput.form.label.diseaseNote')}
                    </TitleLabel>
                    <GContainer alignItems="flex-end">
                      <GItem xs={12}>
                        <TextBox
                          name="diseaseNote"
                          label={translate('facilityReservationTodayConditionInput.form.label.diseaseNote')}
                          maxLength={1000}
                          multiline
                          rows={4}
                          fullWidth
                          control={control}
                        />
                      </GItem>
                    </GContainer>
                  </GItem>
                </>
              )}
            </>
          )}

        {/* きょうの様子 > 予防接種 */}
        {todayConditionInputFieldsInfo &&
          todayConditionInputFieldsInfo.vaccine !== todayConditionInputFieldUsage.noUse && (
            <>
              {/* 予防接種を受けましたか */}
              <GItem>
                <TitleLabel>
                  {todayConditionInputFieldsInfo.vaccine === todayConditionInputFieldUsage.necessary && (
                    <AttentionLabel>*</AttentionLabel>
                  )}
                  {translate('facilityReservationTodayConditionInput.form.label.vaccine')}
                </TitleLabel>
                <GContainer alignItems="flex-end">
                  <GItem xs={12}>
                    <RadioButtonGroup
                      name={'vaccine'}
                      label={translate('facilityReservationTodayConditionInput.form.label.vaccine')}
                      row
                      required={todayConditionInputFieldsInfo.vaccine === todayConditionInputFieldUsage.necessary}
                      buttonValueLabels={yesNos}
                      control={control}
                    />
                  </GItem>
                </GContainer>
              </GItem>

              {isVaccine && (
                <>
                  {/* 予防接種をいつ受けましたか */}
                  <GItem>
                    <TitleLabel>
                      {todayConditionInputFieldsInfo.vaccine === todayConditionInputFieldUsage.necessary &&
                        isVaccine && <AttentionLabel>*</AttentionLabel>}
                      {translate('facilityReservationTodayConditionInput.form.label.vaccineMonthsAgo.field')}
                    </TitleLabel>
                    <GContainer alignItems="flex-end">
                      <GItem xs={12}>
                        <GContainer alignItems="flex-end">
                          <GItem xs={1}>
                            <NumberBox
                              name={'vaccineMonthsAgo'}
                              label={translate(
                                'facilityReservationTodayConditionInput.form.label.vaccineMonthsAgo.field'
                              )}
                              max={12}
                              min={1}
                              placeholder={'1'}
                              required={
                                todayConditionInputFieldsInfo.vaccine === todayConditionInputFieldUsage.necessary &&
                                isVaccine
                              }
                              control={control}
                            />
                          </GItem>
                          <GItem xs="auto" style={{ display: 'flex', alignItems: 'center', padding: '0 8px' }}>
                            {translate('facilityReservationTodayConditionInput.form.label.vaccineMonthsAgo.unit')}
                          </GItem>
                        </GContainer>
                      </GItem>
                    </GContainer>
                  </GItem>

                  {/* 予防接種に関する備考 */}
                  <GItem>
                    <TitleLabel>
                      {translate('facilityReservationTodayConditionInput.form.label.vaccineNote')}
                    </TitleLabel>
                    <GContainer alignItems="flex-end">
                      <GItem xs={12}>
                        <TextBox
                          name="vaccineNote"
                          label={translate('facilityReservationTodayConditionInput.form.label.vaccineNote')}
                          maxLength={1000}
                          multiline
                          rows={4}
                          fullWidth
                          control={control}
                        />
                      </GItem>
                    </GContainer>
                  </GItem>
                </>
              )}
            </>
          )}

        {/* きょうの様子 > 病院受診 */}
        {todayConditionInputFieldsInfo &&
          todayConditionInputFieldsInfo.hospitalVisit !== todayConditionInputFieldUsage.noUse && (
            <>
              {/* 病院を受診しましたか */}
              <GItem>
                <TitleLabel>
                  {todayConditionInputFieldsInfo.hospitalVisit === todayConditionInputFieldUsage.necessary && (
                    <AttentionLabel>*</AttentionLabel>
                  )}
                  {translate('facilityReservationTodayConditionInput.form.label.hospitalVisit')}
                </TitleLabel>
                <GContainer alignItems="flex-end">
                  <GItem xs={12}>
                    <RadioButtonGroup
                      name={'hospitalVisit'}
                      label={translate('facilityReservationTodayConditionInput.form.label.hospitalVisit')}
                      row
                      required={todayConditionInputFieldsInfo.hospitalVisit === todayConditionInputFieldUsage.necessary}
                      buttonValueLabels={yesNos}
                      control={control}
                    />
                  </GItem>
                </GContainer>
              </GItem>

              {isHospitalVisit && (
                <>
                  {/* 病院をいつ受診しましたか */}
                  <GItem>
                    <TitleLabel>
                      {todayConditionInputFieldsInfo.hospitalVisit === todayConditionInputFieldUsage.necessary &&
                        isHospitalVisit && <AttentionLabel>*</AttentionLabel>}
                      {translate('facilityReservationTodayConditionInput.form.label.hospitalVisitMonthsAgo.field')}
                    </TitleLabel>
                    <GContainer alignItems="flex-end">
                      <GItem xs={12}>
                        <GContainer alignItems="flex-end">
                          <GItem xs={1}>
                            <NumberBox
                              name={'hospitalVisitMonthsAgo'}
                              label={translate(
                                'facilityReservationTodayConditionInput.form.label.hospitalVisitMonthsAgo.field'
                              )}
                              max={12}
                              min={1}
                              placeholder={'1'}
                              required={
                                todayConditionInputFieldsInfo.hospitalVisit ===
                                  todayConditionInputFieldUsage.necessary && isHospitalVisit
                              }
                              control={control}
                            />
                          </GItem>
                          <GItem xs="auto" style={{ display: 'flex', alignItems: 'center', padding: '0 8px' }}>
                            {translate('facilityReservationTodayConditionInput.form.label.hospitalVisitMonthsAgo.unit')}
                          </GItem>
                        </GContainer>
                      </GItem>
                    </GContainer>
                  </GItem>

                  {/* 病院受診に関する備考 */}
                  <GItem>
                    <TitleLabel>
                      {translate('facilityReservationTodayConditionInput.form.label.hospitalVisitNote')}
                    </TitleLabel>
                    <GContainer alignItems="flex-end">
                      <GItem xs={12}>
                        <TextBox
                          name="hospitalVisitNote"
                          label={translate('facilityReservationTodayConditionInput.form.label.hospitalVisitNote')}
                          maxLength={1000}
                          multiline
                          rows={4}
                          fullWidth
                          control={control}
                        />
                      </GItem>
                    </GContainer>
                  </GItem>
                </>
              )}
            </>
          )}

        {/* きょうの様子 > 感染症 */}
        {todayConditionInputFieldsInfo &&
          todayConditionInputFieldsInfo.infection !== todayConditionInputFieldUsage.noUse && (
            <>
              {/* 感染症に関する該当事項に該当しますか */}
              <GItem>
                <TitleLabel>
                  {todayConditionInputFieldsInfo.infection === todayConditionInputFieldUsage.necessary && (
                    <AttentionLabel>*</AttentionLabel>
                  )}
                  {translate('facilityReservationTodayConditionInput.form.label.infection')}
                </TitleLabel>
                <GContainer alignItems="flex-end">
                  <GItem xs={12}>
                    <RadioButtonGroup
                      name={'infection'}
                      label={translate('facilityReservationTodayConditionInput.form.label.infection')}
                      row
                      required={todayConditionInputFieldsInfo.infection === todayConditionInputFieldUsage.necessary}
                      buttonValueLabels={yesNos}
                      control={control}
                    />
                  </GItem>
                </GContainer>
              </GItem>

              {/* 感染症に関する備考 */}
              {isInfection && (
                <>
                  <GItem>
                    <TitleLabel>
                      {translate('facilityReservationTodayConditionInput.form.label.infectionNote')}
                    </TitleLabel>
                    <GContainer alignItems="flex-end">
                      <GItem xs={12}>
                        <TextBox
                          name="infectionNote"
                          label={translate('facilityReservationTodayConditionInput.form.label.infectionNote')}
                          maxLength={1000}
                          multiline
                          rows={4}
                          fullWidth
                          control={control}
                        />
                      </GItem>
                    </GContainer>
                  </GItem>
                </>
              )}
            </>
          )}

        {/* きょうの様子 > 本日の体調に関する注意点 */}
        {todayConditionInputFieldsInfo &&
          todayConditionInputFieldsInfo.todayConditionPoint !== todayConditionInputFieldUsage.noUse && (
            <>
              {/* 本日の体調で気になることはありますか */}
              <GItem>
                <TitleLabel>
                  {todayConditionInputFieldsInfo.todayConditionPoint === todayConditionInputFieldUsage.necessary && (
                    <AttentionLabel>*</AttentionLabel>
                  )}
                  {translate('facilityReservationTodayConditionInput.form.label.todayConditionPoint')}
                </TitleLabel>
                <GContainer alignItems="flex-end">
                  <GItem xs={12}>
                    <RadioButtonGroup
                      name={'todayConditionPoint'}
                      label={translate('facilityReservationTodayConditionInput.form.label.todayConditionPoint')}
                      row
                      required={
                        todayConditionInputFieldsInfo.todayConditionPoint === todayConditionInputFieldUsage.necessary
                      }
                      buttonValueLabels={yesNos}
                      control={control}
                    />
                  </GItem>
                </GContainer>
              </GItem>

              {/* 本日の体調に関する備考 */}
              {isTodayConditionPoint && (
                <>
                  <GItem>
                    <TitleLabel>
                      {translate('facilityReservationTodayConditionInput.form.label.todayConditionPointNote')}
                    </TitleLabel>
                    <GContainer alignItems="flex-end">
                      <GItem xs={12}>
                        <TextBox
                          name="todayConditionPointNote"
                          label={translate('facilityReservationTodayConditionInput.form.label.todayConditionPointNote')}
                          maxLength={1000}
                          multiline
                          rows={4}
                          fullWidth
                          control={control}
                        />
                      </GItem>
                    </GContainer>
                  </GItem>
                </>
              )}
            </>
          )}

        {/* きょうの様子 > 散歩 */}
        {todayConditionInputFieldsInfo &&
          todayConditionInputFieldsInfo.stroll !== todayConditionInputFieldUsage.noUse && (
            <GItem>
              {/* 保育園で散歩に連れて行くことは可能ですか */}
              <TitleLabel>
                {todayConditionInputFieldsInfo.stroll === todayConditionInputFieldUsage.necessary && (
                  <AttentionLabel>*</AttentionLabel>
                )}
                {translate('facilityReservationTodayConditionInput.form.label.stroll')}
              </TitleLabel>
              <GContainer alignItems="flex-end">
                <GItem xs={12}>
                  <RadioButtonGroup
                    name={'stroll'}
                    label={translate('facilityReservationTodayConditionInput.form.label.stroll')}
                    row
                    required={todayConditionInputFieldsInfo.stroll === todayConditionInputFieldUsage.necessary}
                    buttonValueLabels={yesNos}
                    control={control}
                  />
                </GItem>
              </GContainer>
            </GItem>
          )}
      </Stack>
    </GItem>
  )
}

export default TodayConditionSec
